import React, { useState, useEffect } from 'react';
import { useToast } from '../../../contexts/ToastContext';
import FormModal from '../../common/FormModal';
import ConfirmModal from '../../common/ConfirmModal';
import FormField from '../../common/FormField';
import FormGrid from '../../common/FormGrid';
import { FaSave, FaEdit, FaTrash, FaTimes, FaPlus, FaSearch, FaMapMarkerAlt, FaKey } from 'react-icons/fa';
import './CatalogoInsumos.css';
import { getApiUrl } from '../../../utils/api';

const CatalogoDestinos = () => {
  const [destinos, setDestinos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [destinoSeleccionado, setDestinoSeleccionado] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [destinoAEliminar, setDestinoAEliminar] = useState(null);
  const toast = useToast();

  const [formulario, setFormulario] = useState({
    nombre: '',
    clave: ''
  });

  useEffect(() => {
    cargarDestinos();
  }, []);

  const cargarDestinos = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'getDestinosSalidaEntrada' })
      });
      const data = await response.json();
      if (data.success) {
        setDestinos(data.data || []);
      } else {
        toast.error(data.message || 'Error al cargar destinos');
      }
    } catch (error) {
      console.error('Error al cargar destinos:', error);
      toast.error('Error al cargar los destinos');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormulario({ ...formulario, [name]: value });
  };

  const nuevoDestino = () => {
    setDestinoSeleccionado(null);
    setFormulario({ nombre: '', clave: '' });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setDestinoSeleccionado(null);
    setFormulario({ nombre: '', clave: '' });
  };

  const seleccionarDestino = (destino) => {
    setDestinoSeleccionado(destino);
  };

  const editarDestino = (destino) => {
    setDestinoSeleccionado(destino);
    setFormulario({
      nombre: destino.nombre || '',
      clave: destino.clave || ''
    });
    setShowModal(true);
  };

  const guardarDestino = async () => {
    if (!formulario.nombre.trim()) {
      toast.error('El nombre del destino es requerido');
      return;
    }

    try {
      setGuardando(true);
      const esEdicion = !!destinoSeleccionado;
      const payload = {
        tipo: esEdicion ? 'actualizarDestinoSalidaEntrada' : 'crearDestinoSalidaEntrada',
        nombre: formulario.nombre,
        clave: formulario.clave
      };

      if (esEdicion) {
        payload.idDestinoSalidaEntrada = destinoSeleccionado.idDestinoSalidaEntrada;
      }

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (data.success) {
        toast.success(esEdicion ? 'Destino actualizado correctamente' : 'Destino creado correctamente');
        handleCloseModal();
        cargarDestinos();
      } else {
        toast.error(data.message || 'Error al guardar el destino');
      }
    } catch (error) {
      console.error('Error al guardar:', error);
      toast.error('Error al guardar el destino');
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminarDestino = (destino) => {
    setDestinoAEliminar(destino);
    setShowDeleteModal(true);
  };

  const eliminarDestino = async () => {
    if (!destinoAEliminar) return;

    try {
      setGuardando(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'eliminarDestinoSalidaEntrada',
          idDestinoSalidaEntrada: destinoAEliminar.idDestinoSalidaEntrada
        })
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Destino eliminado correctamente');
        setShowDeleteModal(false);
        setDestinoAEliminar(null);
        setDestinoSeleccionado(null);
        cargarDestinos();
      } else {
        toast.error(data.message || 'Error al eliminar el destino');
      }
    } catch (error) {
      console.error('Error al eliminar:', error);
      toast.error('Error al eliminar el destino');
    } finally {
      setGuardando(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDestinoAEliminar(null);
  };

  const destinosFiltrados = destinos.filter(destino => {
    if (!busqueda) return true;
    const texto = busqueda.toLowerCase();
    return (
      destino.nombre?.toLowerCase().includes(texto) ||
      destino.clave?.toLowerCase().includes(texto)
    );
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        if (showModal) guardarDestino();
      }
      if (e.key === 'Escape') {
        if (showModal) handleCloseModal();
      }
      if (e.key === 'Delete' && destinoSeleccionado && !showModal && destinoSeleccionado.idDestinoSalidaEntrada !== 1) {
        confirmarEliminarDestino(destinoSeleccionado);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [destinoSeleccionado, formulario, showModal]);

  return (
    <div className="catalogo-proveedores-excel">
      <div className="excel-header">
        <div className="excel-header-controls">
          <div className="excel-search-container">
            <div className="excel-search-group">
              <FaSearch className="search-icon" />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar destinos..."
                className="excel-search-input"
                title="Buscar por nombre o clave"
              />
              {busqueda && (
                <button onClick={() => setBusqueda('')} className="excel-clear-search" title="Limpiar busqueda">
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
          <div className="excel-acciones">
            <button onClick={nuevoDestino} className="btn-excel btn-nuevo" title="Nuevo destino">
              <FaPlus /> Nuevo Destino
            </button>
          </div>
        </div>
      </div>

      <div className="excel-table-container">
        <table className="excel-table">
          <thead>
            <tr>
              <th className="excel-th">ID</th>
              <th className="excel-th">Nombre</th>
              <th className="excel-th">Clave</th>
              <th className="excel-th excel-th-actions">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="excel-loading">Cargando destinos...</td>
              </tr>
            ) : destinosFiltrados.length === 0 ? (
              <tr>
                <td colSpan="4" className="excel-empty">
                  {busqueda ? `No se encontraron destinos que coincidan con "${busqueda}"` : 'No hay destinos registrados'}
                </td>
              </tr>
            ) : (
              destinosFiltrados.map(destino => (
                <tr
                  key={destino.idDestinoSalidaEntrada}
                  className={`excel-row ${destinoSeleccionado?.idDestinoSalidaEntrada === destino.idDestinoSalidaEntrada ? 'excel-row-selected' : ''}`}
                  onClick={() => seleccionarDestino(destino)}
                >
                  <td className="excel-cell excel-cell-xsmall">{destino.idDestinoSalidaEntrada}</td>
                  <td className="excel-cell excel-cell-nombre">{destino.nombre}</td>
                  <td className="excel-cell excel-cell-small">{destino.clave}</td>
                  <td className="excel-cell excel-cell-actions">
                    {destino.idDestinoSalidaEntrada !== 1 && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); editarDestino(destino); }}
                          className="excel-btn-action excel-btn-edit"
                          title="Editar"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); confirmarEliminarDestino(destino); }}
                          className="excel-btn-action excel-btn-delete"
                          title="Eliminar"
                        >
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="excel-footer">
        <div className="excel-info">
          <span>Total de registros: <strong>{destinos.length}</strong></span>
          {busqueda && (
            <span>| Filtrados: <strong>{destinosFiltrados.length}</strong></span>
          )}
          {destinoSeleccionado && (
            <span>| Seleccionado: <strong>{destinoSeleccionado.nombre}</strong></span>
          )}
        </div>
        <div className="excel-shortcuts">
          <span>Nuevo: Crear destino | Enter: Editar | Del: Eliminar</span>
        </div>
      </div>

      <FormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={destinoSeleccionado ? 'Editar Destino' : 'Nuevo Destino'}
        onSave={guardarDestino}
        saveLabel={destinoSeleccionado ? 'Actualizar' : 'Crear'}
        isSaving={guardando}
        size="small"
        saveIcon={<FaMapMarkerAlt />}
        maxHeight="35vh"
      >
        <FormGrid columns={2} gap="12px">
          <FormField
            label="Nombre"
            name="nombre"
            value={formulario.nombre}
            onChange={handleInputChange}
            placeholder="Nombre del destino"
            required
            icon={<FaMapMarkerAlt />}
          />
          <FormField
            label="Clave"
            name="clave"
            value={formulario.clave}
            onChange={handleInputChange}
            placeholder="Clave del destino"
            icon={<FaKey />}
          />
        </FormGrid>
      </FormModal>

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={eliminarDestino}
        title="Eliminar Destino"
        message={`¿Esta seguro de eliminar el destino "${destinoAEliminar?.nombre}"? Esta accion no se puede deshacer.`}
        confirmLabel="Eliminar"
        type="danger"
        isLoading={guardando}
      />
    </div>
  );
};

export default CatalogoDestinos;
